import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Activity, AlertCircle, Loader } from "lucide-react";
import PerformanceFilter from "./PerformanceFilters";
import GraphLayout from "./GraphLayout";
import UnauthorizedPage from "../UnauthorizedPage";
const apiUrl = process.env.REACT_APP_API_URL;

const dashboardQuery = `
  query MyQuery {
  getDashboardData {
    accountNo
    details {
      metrics {
        alarmDetails {
          alarmName
          comparisonOperator
          resourceName
          statistic
          threshold
        }
        metricName
      }
      namespace
      resourceName
    }
    id
    name
    region
  }
}
`;

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow p-8 mt-4">
    <Activity className="h-12 w-12 text-gray-300 mb-4" />
    <h3 className="text-xl font-medium text-gray-700 mb-2">
      No Dashboard Found
    </h3>
    <p className="text-gray-500 text-center">
      There is no dashboard matching your current filter selection. Please try
      different filter options.
    </p>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow p-8 mt-4">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <h3 className="text-xl font-medium text-gray-700 mb-2">
      Error Loading Dashboard Data
    </h3>
    <p className="text-gray-500 text-center mb-4">
      There was a problem loading the dashboard data. Please try again.
    </p>
    <div className="bg-gray-100 p-4 rounded-md w-full max-w-lg overflow-auto">
      <code className="text-sm text-red-600 whitespace-pre-wrap">{error}</code>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="space-y-8">
    {[1, 2].map((section) => (
      <div key={section} className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-6 w-6 bg-gray-200 rounded-full pulse-animation"></div>
          <div className="h-6 w-48 bg-gray-200 rounded pulse-animation"></div>
        </div>
        <div className="flex overflow-x-auto gap-4 no-scrollbar">
          {[1, 2, 3].map((card) => (
            <div
              key={card}
              className="flex-none w-[500px] bg-white rounded-lg shadow p-6 overflow-hidden relative"
            >
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent animate-shimmer"
                style={{ backgroundSize: "200% 100%" }}
              ></div>
              <div className="flex items-center justify-between mb-4">
                <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                <div className="h-6 w-24 bg-gray-200 rounded"></div>
              </div>
              <div className="h-[300px] bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Performance = () => {
  const [selectedAccount, setSelectedAccount] = useState("none");
  const [selectedRegion, setSelectedRegion] = useState("none");
  const [selectedTime, setSelectedTime] = useState("1h");
  const [allDashboards, setAllDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);
  const accessToken = useSelector((state) => state.accessToken.value);
  const api = useMemo(() => axios.create({
    baseURL: apiUrl,
    timeout: 50000,
    headers: {
      Authorization: accessToken,
    }
  }), [accessToken]);

  const fetchAllDashboards = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('', { query: dashboardQuery });
      if (response.data?.data?.getDashboardData) {
        setAllDashboards(response.data.data.getDashboardData);

        return response.data.data.getDashboardData;
      } else if (response.data?.errors) {
        if (
          response.data.errors[0].errorType == "Unauthorized"
        ) {
          setAuthError("Unauthorized");
        };
        const errorMessage = response.data.errors[0]?.message || "Unknown API error";
        setError(errorMessage);
        return null;
      }
    } catch (error) {
      console.error("Error fetching dashboards:", error);
      setError(`Failed to fetch dashboards: ${error.message}`);
      return null;
    } finally {
      if (!selectedAccount || !selectedRegion) {
        setIsLoading(false);
      }
    }
  }, [api, selectedAccount, selectedRegion]);

  const findDashboard = useCallback((dashboards, account, region) => {
    if (!dashboards || !account || !region) return null;
    return dashboards.find(dashboard => (dashboard.accountNo == account && dashboard.region == region));
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      const dashboards = await fetchAllDashboards();
      if (!dashboards) return;

      if (selectedAccount && selectedRegion) {
        const dashboard = findDashboard(dashboards, selectedAccount, selectedRegion);
        setCurrentDashboard(dashboard);
      }

      setIsLoading(false);
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (!selectedAccount || !selectedRegion) {
      setCurrentDashboard(null);
      return;
    }
    const dashboard = findDashboard(allDashboards, selectedAccount, selectedRegion);
    setCurrentDashboard(dashboard);
    setIsLoading(false);
  }, [selectedAccount, selectedRegion, allDashboards, findDashboard]);
if (authError == "Unauthorized") {
    return <UnauthorizedPage />;
  } else{
  return (
    <div className="p-2">
      <PerformanceFilter
        selectedAccount={selectedAccount}
        selectedRegion={selectedRegion}
        selectedTime={selectedTime}
        onAccountChange={setSelectedAccount}
        onRegionChange={setSelectedRegion}
        onTimeChange={setSelectedTime}
      />
      {selectedAccount === 'none' || selectedRegion === 'none' ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <Loader className="h-10 w-10 text-blue-500 animate-spin" />
        </div>
      ) : isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} />
      ) : !currentDashboard ? (
        <EmptyState />
      ) : (
        <GraphLayout
          dashboardData={currentDashboard}
          selectedTime={selectedTime}
          api={api}
        />
      )}
    </div>
  );
}
};

export default Performance;